// src/utils/mockDataCostCodes.ts
// Client Cost Code Library — per-client MRS-based cost code management
// Spec: CostCode_FunctionSpec.docx — Phase 1 data model
//
// TMR cost code structure follows the MRS (Main Road Specification) WBS system
// as defined in TMR PCEM 9th Edition, Annexure C (MRS Work Breakdown Structure).
// Full MRS item IDs and schedule rates are sourced from the TMR Estimate Import
// Template (internal SharePoint). Until imported, the TMR library contains
// illustrative seed items derived from Annexure C MRS group headings and rates
// consistent with existing awarded contract data (TP-2026-001).
//
// CapEx/OpEx classification per TMR PCEM 9th Edition Annexure N.

// ── Types ─────────────────────────────────────────────────────────

export type CostCodeClientType =
  | 'TMR'               // Dept of Transport & Main Roads
  | 'SCC'               // Sunshine Coast Council
  | 'BCC'               // Brisbane City Council
  | 'ENERGEX'           // Energex (Qld distribution network)
  | 'ERGON'             // Ergon Energy
  | 'INTERNAL'          // MCB CE internal cost codes

/** L = Labour · M = Materials · E = Equipment · C = Combined */
export type CostCodeType = 'L' | 'M' | 'E' | 'C'

/** CapEx = capital expenditure · OpEx = operating expenditure (per TMR Annexure N) */
export type ExpenseClass = 'CAPEX' | 'OPEX'

/** PENDING_IMPORT: no items loaded yet — awaiting source template
 *  ILLUSTRATIVE: seed items present for demo — rates indicative only
 *  IMPORTED: loaded from client's official Estimate Import Template
 *  PARTIAL: some MRS groups loaded, others still pending */
export type LibraryImportStatus = 'PENDING_IMPORT' | 'ILLUSTRATIVE' | 'IMPORTED' | 'PARTIAL'

export type AuditAction = 'IMPORT_BULK' | 'ADD_SINGLE' | 'EDIT' | 'DEACTIVATE' | 'ACTIVATE' | 'RATE_UPDATE'

// ── Interfaces ────────────────────────────────────────────────────

export interface CostCodeItem {
  id:               string
  mrsGroup:         string    // e.g. 'MRS93' — TMR group-level code from Annexure C
  mrsGroupDesc:     string    // e.g. 'Traffic Signals'
  workItemId:       string    // e.g. '93001L' — numeric item ID within group
  description:      string    // Full client-published description
  unit:             string    // ea / m / m² / hr / LS / week / day
  rateExGST:        number    // Client's published schedule rate per unit (ex GST)
  type:             CostCodeType
  expenseClass:     ExpenseClass
  wiRef?:           string    // Optional: linked client WI document ref (e.g. 'TMR MRS SD ITS-0042')
  effectiveDate:    string    // ISO date — when this rate took effect
  isActive:         boolean
  scheduleVersion:  string    // e.g. 'TMR PCEM v4.2 — Jan 2026'
  notes?:           string
  isIllustrative?:  boolean   // true = seed/demo rate, not from official template
}

export interface LibraryAuditEntry {
  id:         string
  timestamp:  string
  userId:     string
  userName:   string
  action:     AuditAction
  description:string
  batchRef?:  string    // bulk import batch reference
  itemCount?: number    // items affected in a bulk import
}

export interface ClientCostLibrary {
  clientId:         string
  clientName:       string
  clientType:       CostCodeClientType
  importStatus:     LibraryImportStatus
  scheduleVersion?: string   // e.g. 'TMR PCEM 9th Ed, MRS v4.2 Jan 2026'
  importedAt?:      string   // ISO datetime of last import
  importedByName?:  string
  templateSource:   string   // Where to obtain the import template
  items:            CostCodeItem[]
  auditLog:         LibraryAuditEntry[]
}

// ── Display metadata ──────────────────────────────────────────────

export const CLIENT_TYPE_META: Record<CostCodeClientType, {
  label: string; abbr: string; bg: string; text: string; border: string
}> = {
  TMR:      { label: 'Dept of Transport & Main Roads', abbr: 'TMR',     bg: 'bg-blue-50',   text: 'text-blue-800',   border: 'border-blue-200'   },
  SCC:      { label: 'Sunshine Coast Council',          abbr: 'SCC',     bg: 'bg-teal-50',   text: 'text-teal-800',   border: 'border-teal-200'   },
  BCC:      { label: 'Brisbane City Council',           abbr: 'BCC',     bg: 'bg-indigo-50', text: 'text-indigo-800', border: 'border-indigo-200' },
  ENERGEX:  { label: 'Energex',                         abbr: 'Energex', bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-200'  },
  ERGON:    { label: 'Ergon Energy',                    abbr: 'Ergon',   bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-200' },
  INTERNAL: { label: 'MCB CE Internal',                 abbr: 'Internal',bg: 'bg-gray-50',   text: 'text-gray-700',   border: 'border-gray-200'   },
}

export const COST_CODE_TYPE_META: Record<CostCodeType, { label: string; bg: string; text: string }> = {
  L: { label: 'Labour',    bg: 'bg-blue-100',   text: 'text-blue-800'   },
  M: { label: 'Materials', bg: 'bg-green-100',  text: 'text-green-800'  },
  E: { label: 'Equipment', bg: 'bg-purple-100', text: 'text-purple-800' },
  C: { label: 'Combined',  bg: 'bg-gray-100',   text: 'text-gray-700'   },
}

export const EXPENSE_CLASS_META: Record<ExpenseClass, { label: string; bg: string; text: string }> = {
  CAPEX: { label: 'CapEx', bg: 'bg-[#1F3864]/10', text: 'text-[#1F3864]' },
  OPEX:  { label: 'OpEx',  bg: 'bg-amber-100',    text: 'text-amber-800'  },
}

export const IMPORT_STATUS_META: Record<LibraryImportStatus, {
  label: string; bg: string; text: string; border: string; description: string
}> = {
  PENDING_IMPORT: {
    label: 'Pending import',
    bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200',
    description: 'No cost codes loaded. Import the client\'s Estimate Import Template to enable AI cost code pre-fill on BOQ lines and claim validation.',
  },
  ILLUSTRATIVE: {
    label: 'Illustrative data',
    bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200',
    description: 'Illustrative seed codes loaded for demonstration. Rates and item IDs are indicative — import the official Estimate Import Template before live tender or claim use.',
  },
  IMPORTED: {
    label: 'Imported',
    bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-200',
    description: 'Cost code library loaded from the client\'s official schedule. AI agents can pre-fill and validate cost codes.',
  },
  PARTIAL: {
    label: 'Partial import',
    bg: 'bg-purple-50', text: 'text-purple-800', border: 'border-purple-200',
    description: 'Some MRS groups are loaded; others are pending. Check which groups are missing before submitting tenders or claims.',
  },
}

// ── MRS group registry (from TMR PCEM Annexure C) ─────────────────
// Relevant to MCB CE ITS / electrical / civil scope

export const TMR_MRS_GROUPS: { code: string; description: string; expenseClass: ExpenseClass }[] = [
  { code: 'MRS02',  description: 'Provision for Traffic',                                         expenseClass: 'CAPEX' },
  { code: 'MRS28',  description: 'Contractor\'s Site Facilities and Camp',                        expenseClass: 'OPEX'  },
  { code: 'MRS50',  description: 'Specific Quality System Requirements',                          expenseClass: 'OPEX'  },
  { code: 'MRS91',  description: 'Conduits and Pits',                                             expenseClass: 'CAPEX' },
  { code: 'MRS92',  description: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment', expenseClass: 'CAPEX' },
  { code: 'MRS93',  description: 'Traffic Signals',                                               expenseClass: 'CAPEX' },
  { code: 'MRS94',  description: 'Road Lighting',                                                 expenseClass: 'CAPEX' },
  { code: 'MRS140', description: 'Horizontal Directional Drilling (HDD)',                         expenseClass: 'CAPEX' },
  { code: 'MRS202', description: 'Variable Message Signs',                                        expenseClass: 'CAPEX' },
  { code: 'MRS204', description: 'Vehicle Detectors',                                             expenseClass: 'CAPEX' },
  { code: 'MRS226', description: 'Telecommunications Field Cabinets',                             expenseClass: 'CAPEX' },
  { code: 'MRS227', description: 'Changeable Message Signs',                                      expenseClass: 'CAPEX' },
  { code: 'MRS228', description: 'Electrical Switchboards',                                       expenseClass: 'CAPEX' },
  { code: 'MRS232', description: 'Provision of Field Processors',                                 expenseClass: 'CAPEX' },
  { code: 'MRS234', description: 'Communications Cables',                                         expenseClass: 'CAPEX' },
  { code: 'MRS245', description: 'ITS Telecommunications Network (ITS TN)',                       expenseClass: 'CAPEX' },
  { code: 'MRS256', description: 'Power Cables',                                                  expenseClass: 'CAPEX' },
  { code: 'MRS260', description: 'Temporary Variable Speed Limit Signs',                          expenseClass: 'CAPEX' },
  { code: 'MRS263', description: 'Standalone Solar (PV) Power Systems',                          expenseClass: 'CAPEX' },
]

// ── Illustrative TMR seed items ───────────────────────────────────
// Derived from: Annexure C MRS group headings + rates consistent with
// awarded contract TP-2026-001 BOQ (tdr001BOQ in mockDataEstimating.ts).
// Work Item IDs use MRS-group prefix + sequential suffix (L=labour, M=materials).
// IMPORTANT: These are illustrative. Import the TMR Estimate Import Template
// (available from your TMR project manager or estimating.support@tmr.qld.gov.au)
// to replace with official item IDs and current schedule rates.

const ILLUSTRATIVE_NOTE = 'Illustrative rate — confirm against TMR Estimate Import Template'
const SCHEDULE_VER = 'TMR PCEM 9th Ed (illustrative)'
const EFF_DATE = '2026-01-01'

const TMR_SEED_ITEMS: CostCodeItem[] = [

  // ── MRS02: Provision for Traffic ──────────────────────────────
  {
    id: 'tmr-02-01', mrsGroup: 'MRS02', mrsGroupDesc: 'Provision for Traffic',
    workItemId: '20019C', description: 'Traffic management — implementation, maintenance & removal (single-lane alternating)',
    unit: 'day', rateExGST: 880, type: 'C', expenseClass: 'CAPEX',
    wiRef: 'TMR Traffic Control Plan', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-02-02', mrsGroup: 'MRS02', mrsGroupDesc: 'Provision for Traffic',
    workItemId: '20017C', description: 'Traffic management plan — preparation and design',
    unit: 'LS', rateExGST: 4500, type: 'C', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS28: Site Facilities ─────────────────────────────────────
  {
    id: 'tmr-28-01', mrsGroup: 'MRS28', mrsGroupDesc: "Contractor's Site Facilities and Camp",
    workItemId: '28001C', description: 'Site establishment, mob/demob, safety plan, SWMS',
    unit: 'LS', rateExGST: 4500, type: 'C', expenseClass: 'OPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-28-02', mrsGroup: 'MRS28', mrsGroupDesc: "Contractor's Site Facilities and Camp",
    workItemId: '28002C', description: 'Site supervision / Project Coordinator — per week',
    unit: 'week', rateExGST: 2400, type: 'C', expenseClass: 'OPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS91: Conduits and Pits ──────────────────────────────────
  {
    id: 'tmr-91-01', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91001L', description: 'UPVC 100mm conduit — install, labour only (per MRTS91)',
    unit: 'm', rateExGST: 8, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0091', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-91-02', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91001M', description: 'UPVC 100mm conduit + fittings — supply only',
    unit: 'm', rateExGST: 18, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-91-03', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91002L', description: 'UPVC 150mm conduit — install, labour only (per MRTS91)',
    unit: 'm', rateExGST: 11, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0091', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-91-04', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91002M', description: 'UPVC 150mm conduit + fittings — supply only',
    unit: 'm', rateExGST: 26, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-91-05', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91010C', description: 'Cable pit UPVC 300×450 with trafficable lid — supply & install',
    unit: 'ea', rateExGST: 950, type: 'C', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-91-06', mrsGroup: 'MRS91', mrsGroupDesc: 'Conduits and Pits',
    workItemId: '91011C', description: 'Cable pit UPVC 600×600 with trafficable lid — supply & install',
    unit: 'ea', rateExGST: 1450, type: 'C', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS92: Footings ───────────────────────────────────────────
  {
    id: 'tmr-92-01', mrsGroup: 'MRS92', mrsGroupDesc: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment',
    workItemId: '92001C', description: 'Signal pole footing Type F-1 — supply & install (per MRTS92)',
    unit: 'ea', rateExGST: 1200, type: 'C', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0092', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-92-02', mrsGroup: 'MRS92', mrsGroupDesc: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment',
    workItemId: '92002L', description: 'Signal pole footing Type F-2 — install, labour only (per MRTS92)',
    unit: 'ea', rateExGST: 320, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0092', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-92-03', mrsGroup: 'MRS92', mrsGroupDesc: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment',
    workItemId: '92002M', description: 'Signal pole footing Type F-2 — concrete & reo supply',
    unit: 'ea', rateExGST: 680, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-92-04', mrsGroup: 'MRS92', mrsGroupDesc: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment',
    workItemId: '92003L', description: 'Signal pole footing Type F-3 — install, labour only (per MRTS92)',
    unit: 'ea', rateExGST: 340, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0092', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-92-05', mrsGroup: 'MRS92', mrsGroupDesc: 'Footings — Road Lighting, Traffic Signal & Roadside Equipment',
    workItemId: '92003M', description: 'Signal pole footing Type F-3 — concrete & reo supply',
    unit: 'ea', rateExGST: 720, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS93: Traffic Signals ─────────────────────────────────────
  {
    id: 'tmr-93-01', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93001L', description: 'LED 3-aspect signal head — mount, align & wire, labour only (per MRTS253)',
    unit: 'ea', rateExGST: 380, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0253', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-93-02', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93001M', description: 'LED 3-aspect signal head (TMR TAER approved) — supply',
    unit: 'ea', rateExGST: 680, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-93-03', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93010L', description: 'R900S SCATS controller cabinet — fit-out & SCATS config, labour only (per MRTS255)',
    unit: 'item', rateExGST: 3600, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0255', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-93-04', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93010M', description: 'R900S SCATS controller cabinet — supply (TMR nominated supplier)',
    unit: 'item', rateExGST: 14800, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-93-05', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93020L', description: 'SCATS functional test & TMR ITS acceptance — labour (per MRTS93 cl.8)',
    unit: 'item', rateExGST: 2800, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0093', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-93-06', mrsGroup: 'MRS93', mrsGroupDesc: 'Traffic Signals',
    workItemId: '93030L', description: 'Signal pole — erect & set (excludes footing), labour only',
    unit: 'ea', rateExGST: 420, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0253', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS94: Road Lighting ───────────────────────────────────────
  {
    id: 'tmr-94-01', mrsGroup: 'MRS94', mrsGroupDesc: 'Road Lighting',
    workItemId: '94001L', description: 'Street light pole — erect & set, labour only',
    unit: 'ea', rateExGST: 390, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0094', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-94-02', mrsGroup: 'MRS94', mrsGroupDesc: 'Road Lighting',
    workItemId: '94002L', description: 'LED luminaire — mount, aim & wire, labour only',
    unit: 'ea', rateExGST: 220, type: 'L', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS204: Vehicle Detectors ──────────────────────────────────
  {
    id: 'tmr-204-01', mrsGroup: 'MRS204', mrsGroupDesc: 'Vehicle Detectors',
    workItemId: '204001L', description: 'Traffic signal loop detector — installation, labour only',
    unit: 'ea', rateExGST: 285, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0042', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-204-02', mrsGroup: 'MRS204', mrsGroupDesc: 'Vehicle Detectors',
    workItemId: '204001M', description: 'Traffic signal loop detector — materials, per detector',
    unit: 'ea', rateExGST: 415, type: 'M', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0042', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-204-03', mrsGroup: 'MRS204', mrsGroupDesc: 'Vehicle Detectors',
    workItemId: '204010L', description: 'Radar/microwave vehicle detector — installation, labour only',
    unit: 'ea', rateExGST: 340, type: 'L', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS234: Communications Cables ──────────────────────────────
  {
    id: 'tmr-234-01', mrsGroup: 'MRS234', mrsGroupDesc: 'Communications Cables',
    workItemId: '234001L', description: 'Fibre optic cable — pull & splice (per MRTS234), labour only',
    unit: 'splice', rateExGST: 280, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0234', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-234-02', mrsGroup: 'MRS234', mrsGroupDesc: 'Communications Cables',
    workItemId: '234002L', description: 'OTDR test & documentation, per cable run',
    unit: 'ea', rateExGST: 420, type: 'L', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },

  // ── MRS256: Power Cables ───────────────────────────────────────
  {
    id: 'tmr-256-01', mrsGroup: 'MRS256', mrsGroupDesc: 'Power Cables',
    workItemId: '256001L', description: '3C+E 6mm² power cable — pull & terminate, labour only (per MRTS256)',
    unit: 'm', rateExGST: 6, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0256', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-256-02', mrsGroup: 'MRS256', mrsGroupDesc: 'Power Cables',
    workItemId: '256001M', description: '3C+E 6mm² Olex power cable — supply',
    unit: 'm', rateExGST: 14, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-256-03', mrsGroup: 'MRS256', mrsGroupDesc: 'Power Cables',
    workItemId: '256002L', description: '3C+E 16mm² power cable — pull & terminate, labour only (per MRTS256)',
    unit: 'm', rateExGST: 9, type: 'L', expenseClass: 'CAPEX',
    wiRef: 'TMR MRS SD ITS-0256', effectiveDate: EFF_DATE, isActive: true,
    scheduleVersion: SCHEDULE_VER, notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
  {
    id: 'tmr-256-04', mrsGroup: 'MRS256', mrsGroupDesc: 'Power Cables',
    workItemId: '256002M', description: '3C+E 16mm² Olex power cable — supply',
    unit: 'm', rateExGST: 28, type: 'M', expenseClass: 'CAPEX',
    effectiveDate: EFF_DATE, isActive: true, scheduleVersion: SCHEDULE_VER,
    notes: ILLUSTRATIVE_NOTE, isIllustrative: true,
  },
]

// ── In-memory store ───────────────────────────────────────────────

let _libraries: ClientCostLibrary[] = [
  {
    clientId: 'tmr',
    clientName: 'Department of Transport and Main Roads',
    clientType: 'TMR',
    importStatus: 'ILLUSTRATIVE',
    scheduleVersion: 'TMR PCEM 9th Edition — Annexure C (illustrative)',
    templateSource:
      'Obtain from your TMR project manager via the Project Management Hub (SharePoint), ' +
      'or request from estimating.support@tmr.qld.gov.au. ' +
      'Reference: TMR PCEM 9th Edition, Annexure C — Estimate Import Template.',
    items: TMR_SEED_ITEMS,
    auditLog: [
      {
        id: 'al-001', timestamp: '2026-07-01T08:00:00Z',
        userId: 'u2', userName: 'Jane Smith',
        action: 'IMPORT_BULK',
        description: 'Illustrative seed data loaded — MRS91/92/93/94/204/234/256 groups. ' +
                     'Rates derived from awarded contract TP-2026-001 BOQ. ' +
                     'Pending official import from TMR Estimate Import Template.',
        batchRef: 'SEED-2026-001', itemCount: TMR_SEED_ITEMS.length,
      },
    ],
  },
  {
    clientId: 'scc',
    clientName: 'Sunshine Coast Council',
    clientType: 'SCC',
    importStatus: 'PENDING_IMPORT',
    templateSource:
      'Request SCC Schedule of Rates from the SCC Capital Works team or your project manager. ' +
      'Each RFT package includes a pricing schedule — use the pricing form columns as the import template.',
    items: [],
    auditLog: [],
  },
  {
    clientId: 'energex',
    clientName: 'Energex',
    clientType: 'ENERGEX',
    importStatus: 'PENDING_IMPORT',
    templateSource:
      'Request Energex Schedule of Rates (TECs — Technical and Engineering Conditions) from your ' +
      'Energex project coordinator. Cost codes follow Energex\'s internal TEC reference numbering.',
    items: [],
    auditLog: [],
  },
]

let _nextId = 1000

// ── Helper functions ──────────────────────────────────────────────

export function getAllLibraries(): ClientCostLibrary[] {
  return [..._libraries]
}

export function getLibrary(clientId: string): ClientCostLibrary | undefined {
  return _libraries.find(l => l.clientId === clientId)
}

export function getActiveItems(clientId: string): CostCodeItem[] {
  return getLibrary(clientId)?.items.filter(i => i.isActive) ?? []
}

export function lookupByWIRef(clientId: string, wiRef: string): CostCodeItem[] {
  return getActiveItems(clientId).filter(i =>
    i.wiRef && i.wiRef.toLowerCase().includes(wiRef.toLowerCase())
  )
}

export function lookupByMRSGroup(clientId: string, mrsGroup: string): CostCodeItem[] {
  return getActiveItems(clientId).filter(i =>
    i.mrsGroup.toLowerCase() === mrsGroup.toLowerCase()
  )
}

export function lookupByKeyword(clientId: string, keywords: string): {
  item: CostCodeItem; confidence: 'HIGH' | 'MEDIUM' | 'LOW'
}[] {
  const words = keywords.toLowerCase().split(/\s+/).filter(Boolean)
  const items = getActiveItems(clientId)
  return items
    .map(item => {
      const hay = `${item.description} ${item.mrsGroupDesc} ${item.wiRef ?? ''}`.toLowerCase()
      const matches = words.filter(w => hay.includes(w)).length
      const confidence: 'HIGH' | 'MEDIUM' | 'LOW' =
        matches >= words.length * 0.8 ? 'HIGH' :
        matches >= words.length * 0.5 ? 'MEDIUM' : 'LOW'
      return { item, confidence, matches }
    })
    .filter(r => r.matches > 0)
    .sort((a, b) => b.matches - a.matches)
    .slice(0, 5)
    .map(({ item, confidence }) => ({ item, confidence }))
}

export function addCostCodeItem(
  clientId: string,
  item: Omit<CostCodeItem, 'id'>,
  userName: string,
  userId: string = 'u-sys'
): CostCodeItem | null {
  const lib = _libraries.find(l => l.clientId === clientId)
  if (!lib) return null
  const newItem: CostCodeItem = { ...item, id: `cc-${++_nextId}` }
  lib.items.push(newItem)
  lib.auditLog.unshift({
    id: `al-${++_nextId}`,
    timestamp: new Date().toISOString(),
    userId, userName,
    action: 'ADD_SINGLE',
    description: `Added: ${newItem.mrsGroup} ${newItem.workItemId} — ${newItem.description.slice(0, 60)}`,
  })
  return newItem
}

export function deactivateItem(
  clientId: string,
  itemId: string,
  userName: string,
  userId: string = 'u-sys'
): void {
  const lib = _libraries.find(l => l.clientId === clientId)
  if (!lib) return
  const item = lib.items.find(i => i.id === itemId)
  if (!item) return
  item.isActive = false
  lib.auditLog.unshift({
    id: `al-${++_nextId}`,
    timestamp: new Date().toISOString(),
    userId, userName,
    action: 'DEACTIVATE',
    description: `Deactivated: ${item.mrsGroup} ${item.workItemId} — ${item.description.slice(0, 50)}`,
  })
}

export interface ImportResult {
  added:     number
  updated:   number
  unchanged: number
  errors:    string[]
  batchRef:  string
}

/** Simulates Excel bulk import — in production, parses the actual uploaded file */
export function simulateBulkImport(
  clientId: string,
  items: Omit<CostCodeItem, 'id'>[],
  userName: string,
  userId: string = 'u-sys',
  scheduleVersion?: string,
): ImportResult {
  const lib = _libraries.find(l => l.clientId === clientId)
  if (!lib) return { added: 0, updated: 0, unchanged: 0, errors: ['Library not found'], batchRef: '' }

  const batchRef = `IMP-${clientId.toUpperCase()}-${Date.now()}`
  let added = 0; let updated = 0; let unchanged = 0; const errors: string[] = []

  for (const newItem of items) {
    const existing = lib.items.find(i => i.workItemId === newItem.workItemId && i.mrsGroup === newItem.mrsGroup)
    if (!existing) {
      lib.items.push({ ...newItem, id: `cc-${++_nextId}`, isIllustrative: false })
      added++
    } else if (existing.rateExGST !== newItem.rateExGST || existing.description !== newItem.description) {
      existing.rateExGST   = newItem.rateExGST
      existing.description = newItem.description
      existing.isIllustrative = false
      updated++
    } else {
      unchanged++
    }
  }

  if (scheduleVersion) lib.scheduleVersion = scheduleVersion
  lib.importStatus = 'IMPORTED'
  lib.importedAt = new Date().toISOString()
  lib.importedByName = userName

  lib.auditLog.unshift({
    id: `al-${++_nextId}`,
    timestamp: new Date().toISOString(),
    userId, userName,
    action: 'IMPORT_BULK',
    description: `Bulk import from Estimate Import Template: ${added} added, ${updated} updated, ${unchanged} unchanged.`,
    batchRef,
    itemCount: added + updated,
  })

  return { added, updated, unchanged, errors, batchRef }
}

export function addClientLibrary(
  clientId: string,
  clientName: string,
  clientType: CostCodeClientType,
  templateSource: string,
): ClientCostLibrary {
  const newLib: ClientCostLibrary = {
    clientId, clientName, clientType,
    importStatus: 'PENDING_IMPORT',
    templateSource, items: [], auditLog: [],
  }
  _libraries.push(newLib)
  return newLib
}

/** Returns a summary for the nav badge — total illustrative or pending libraries */
export function getCostCodeAlertCount(): number {
  return _libraries.filter(l =>
    l.importStatus === 'PENDING_IMPORT' || l.importStatus === 'ILLUSTRATIVE'
  ).length
}
